import type { Suit } from '@durak/shared';

/**
 * Face-card art for J / Q / K.
 *
 * Two assets ship per card under /public/cards/:
 *   <suit>_<rank>-sm.webp   ~12 KB  256×384 px  (in-game, default)
 *   <suit>_<rank>.webp     ~90 KB   848×1264 px (high-DPI / large displays / hero)
 *
 * The <img> uses srcset so retina+ screens can opt up to the full-res asset
 * when the rendered size justifies it, while every other use case sticks to
 * the small variant and keeps the page lightweight.
 */

interface Props {
  rank: 'J' | 'Q' | 'K';
  suit: Suit;
  /** When true, always load the full-resolution art (useful for hero / profile pages). */
  large?: boolean;
}

const RANK_TO_NAME: Record<Props['rank'], string> = {
  J: 'jack',
  Q: 'queen',
  K: 'king',
};

export function FaceCardArt({ rank, suit, large }: Props) {
  const base = `/cards/${suit}_${RANK_TO_NAME[rank]}`;
  const small = `${base}-sm.webp`;
  const full = `${base}.webp`;
  return (
    <img
      src={large ? full : small}
      srcSet={large ? undefined : `${small} 1x, ${full} 2x`}
      alt=""
      aria-hidden
      draggable={false}
      decoding="async"
      style={{
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    />
  );
}

/** All 12 small face-card URLs — used by the prefetch helper. */
export const FACE_CARD_SMALL_URLS: string[] = (() => {
  const suits: Suit[] = ['spades', 'hearts', 'diamonds', 'clubs'];
  const ranks: Array<Props['rank']> = ['J', 'Q', 'K'];
  const urls: string[] = [];
  for (const s of suits) for (const r of ranks) urls.push(`/cards/${s}_${RANK_TO_NAME[r]}-sm.webp`);
  return urls;
})();
